import { ComponentDidCatch } from '@shared/Frontend/componentDidCatch'
import { ErrorPage } from '@shared/Frontend/components/errorPage'
import { useEventListener } from '@shared/Frontend/hooks/useEventListener'
import { usePrintListeners } from '@shared/Frontend/hooks/usePrintListeners'
import { CookiebarTemplate, CookiebarView } from '@shared/Frontend/libraries/cookiebar/cookiebar.template'
import {
  NetworkStatusTemplate,
  NetworkStatusView,
} from '@shared/Frontend/libraries/networkStatus/networkStatus.template'
import { Router } from '@shared/Frontend/router'
import { getBreakpoint } from '@shared/Frontend/utils/breakpoints'
import { PageLocalizationLinks } from '@shared/Frontend/utils/routes.shared'
import { fieldReplacer, fieldUpdater } from '@shared/Frontend/utils/updaters'
import { getViewport } from '@shared/Frontend/utils/viewport'
import { Template, unit, Unit } from 'ballerina-core'
import React, { ReactNode } from 'react'
import { aboutUsWidget } from '../../aboutUs/aboutUs.widget'
import { application } from '../../allIncludedModules'
import { contactUsWidget } from '../../contactUs/contactUs.widget'
import { dateTestWidget } from '../../dateTest/date.widget'
import { Pages, PageState } from '../../page/page.state'
import { routes } from '../../page/page.utils'
import { MonadsTestTemplate, MonadsTestView } from '../monadsTest/monadsTest.template'
import { RootState } from './root.state'
import i18next from 'i18next'
import { navigate } from 'widgets-for-react'

export type RootView = {
  Main: (_: {
    menuOpen: boolean
    pageKind: PageState['kind']
    isFullScreenPage: boolean
    children: ReactNode
  }) => ReactNode
  SiteFooter: (_: { children: ReactNode; menuOpen: boolean }) => ReactNode
  SiteHeader: (_: {
    menuOpen: boolean
    toggleMenu: (_: boolean) => void
    pageKind: PageState['kind']
    languages: Array<string>
    localizationLinks?: PageLocalizationLinks
    children: ReactNode
  }) => ReactNode
  HomePage: () => ReactNode
  NotFoundPage: () => ReactNode
} & MonadsTestView &
  CookiebarView &
  NetworkStatusView

export const RootTemplate = Template.Default<Unit, RootState, Unit, RootView>(({ context, setState, view }) => {
  useEventListener('resize', (): void =>
    setState(
      (state): RootState =>
        fieldReplacer<RootState>()('breakpoint')(getBreakpoint())(
          fieldReplacer<RootState>()('viewport')(getViewport())(state)
        )
    )
  )
  usePrintListeners((newPrintMode): void => setState(fieldReplacer<RootState>()('printMode')(newPrintMode)))
  const isFullScreenPage = application.fullScreenPages.some((p) => p == context.page.kind)
  return (
    <>
      <MappedCookiebarTemplate context={context.cookiebar} setState={setState} foreignMutations={unit} view={view} />
      {application.roots<RootState, Pages>(context, setState)}
      <Router routes={routes} setState={setState} />
      {window.isPWA && (
        <MappedNetworkStatusTemplate
          context={context.networkStatus}
          setState={setState}
          foreignMutations={unit}
          view={view}
        />
      )}
      {!isFullScreenPage && (
        <view.SiteHeader
          pageKind={context.page.kind}
          languages={window.languages}
          localizationLinks={context.page.localizationLinks}
          menuOpen={context.menuOpen}
          toggleMenu={(newIsOpen) => setState(fieldReplacer<RootState>()('menuOpen')(newIsOpen))}
        >
          {application.headers<RootState, Pages>(context, setState)}
        </view.SiteHeader>
      )}
      <view.Main
        menuOpen={context.menuOpen}
        pageKind={context.page.kind}
        isFullScreenPage={application.fullScreenPages.some((p) => p === context.page.kind)}
      >
        <ComponentDidCatch key={context.page.kind} fallback={ErrorPage}>
          <PageSwitch context={context} setState={setState} foreignMutations={unit} view={view} />
        </ComponentDidCatch>
      </view.Main>
      {!isFullScreenPage && (
        <view.SiteFooter menuOpen={context.menuOpen}>
          {application.footers<RootState, Pages>(context, setState)}
        </view.SiteFooter>
      )}
    </>
  )
})

const PageSwitch = Template.Default<Unit, RootState, Unit, RootView>(({ context, setState, view }): JSX.Element => {
  const w = application.tryPageSwitch<RootState, Pages>(context, setState)
  if (w != undefined) return w
  const { page, aboutUs, contactUs, dateTest } = context
  switch (page.kind) {
    case 'home':
      navigate(`/${i18next.language}`) // Ensure we go to the language specific homepage
      return <></>
    case 'errorHandlingTest':
      return <>{JSON.stringify(context)}</>
    case 'aboutUs':
      return aboutUsWidget(aboutUs).map(fieldUpdater<RootState>()('aboutUs')).run(setState)
    case 'contactUs':
      return contactUsWidget(contactUs).map(fieldUpdater<RootState>()('contactUs')).run(setState)
    case 'datesTest':
      return dateTestWidget(dateTest).map(fieldUpdater<RootState>()('dateTest')).run(setState)
    case 'monadsTest':
      return <MappedMonadsTemplate context={page.monadsTest} setState={setState} foreignMutations={unit} view={view} />
    default:
      return <view.NotFoundPage />
  }
})

const MappedMonadsTemplate = MonadsTestTemplate.mapState<RootState>((updater) =>
  fieldUpdater<RootState>()('page')((state) =>
    state.kind == 'monadsTest'
      ? fieldUpdater<PageState & { kind: 'monadsTest' }>()('monadsTest')(updater)(state)
      : state
  )
)

const MappedNetworkStatusTemplate = NetworkStatusTemplate.mapState<RootState>(
  fieldUpdater<RootState>()('networkStatus')
)

const MappedCookiebarTemplate = CookiebarTemplate.mapState(fieldUpdater<RootState>()('cookiebar'))
